import { API_URLS } from '@helpers/api';
import { imageUrl } from '@helpers/image';
import { isSSR } from '@helpers/ssr';
import useBodyScrollLock from '@hooks/useBodyScrollLock';
import HubspotForm from '@stories/Components/Forms/HubspotForm/HubspotForm';
import Modal from '@stories/Components/Misc/Modal/Modal';
import * as React from 'react';
import S from './ModalForm.styles';
import { withMotionMax } from '@hoc/withMotionMax';
import { useAnimation } from 'framer-motion';
const ModalForm = ({ opened = false, ...props }) => {
    const [status, setStatus] = React.useState('idle');
    const { postSubmitAction } = props.config.configuration;
    const [isOpen, setIsOpen] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);
    const prevScrollY = React.useRef(0);
    const animation = useAnimation();
    React.useEffect(() => {
        if (opened) {
            setIsOpen(true);
        }
    }, [opened]);
    React.useEffect(() => {
        const handleScroll = () => {
            // animate every 15% of page scrolled unless currently animating
            const currentScrollY = window.scrollY;
            const pageHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
            const scrolledPercentage = (currentScrollY / pageHeight) * 100;
            const scrolledPercentageRounded = Math.floor(scrolledPercentage / 15) * 15;
            // reset if user scrolls back to top
            if (scrolledPercentageRounded < 1) {
                prevScrollY.current = scrolledPercentageRounded;
            }
            if (scrolledPercentageRounded > prevScrollY.current) {
                prevScrollY.current = scrolledPercentageRounded;
                if (!isAnimating) {
                    animation.start('scrolled');
                }
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isAnimating]);
    const scrollPosition = !isSSR() && isOpen ? window.scrollY : 0;
    const restoreScroll = () => window?.scrollTo(0, scrollPosition);
    const ref = useBodyScrollLock(isOpen);
    const ctaVariants = {
        scrolled: {
            scale: 1.1,
            transition: {
                duration: 0.6,
                ease: 'easeInOut',
                repeat: 8.5,
                repeatType: 'reverse',
            },
        },
    };
    const handleAnimationStart = () => {
        setIsAnimating(true);
    };
    const handleAnimationComplete = () => {
        setIsAnimating(false);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(S.CtaWrapper, { variants: ctaVariants, animate: animation, onAnimationStart: handleAnimationStart, onAnimationComplete: handleAnimationComplete },
            React.createElement(S.Cta, { mode: "button", showIcon: isOpen, theme: isOpen ? 'white' : 'green', type: "button", onClick: (event) => {
                    event.preventDefault();
                    if (isOpen) {
                        setIsOpen(false);
                        restoreScroll();
                    }
                    else {
                        setIsOpen(true);
                    }
                } }, isOpen ? 'Close Form' : props.floatingCtaLabel)),
        React.createElement(Modal, { ariaLabel: "", closeLabel: "Close Form", isOpen: isOpen, showBackground: true, showClose: false, onClose: () => {
                window.location.hash = '';
                restoreScroll();
            } },
            React.createElement(S.Container, null,
                React.createElement(S.Header, null,
                    React.createElement(S.Title, null, props.title),
                    props.logo && (React.createElement(S.LogoWrapper, null,
                        React.createElement(S.Logo, { alt: props.logo.altText, loading: "lazy", role: "presentation", src: imageUrl(props.logo, {}) })))),
                React.createElement(S.Inner, { ref: ref },
                    props.content && React.createElement(S.Content, null, props.content),
                    status === 'fulfilled' && postSubmitAction.type === 'thank_you' && (React.createElement(S.Content, null,
                        React.createElement("p", null, postSubmitAction.value))),
                    status !== 'fulfilled' && (React.createElement(S.FormWrapper, null,
                        React.createElement(HubspotForm, { buttonPosition: "left", config: props.config, defaultValues: props.defaultValues, disabled: status === 'pending', onSubmitValid: async (formData) => {
                                setStatus('pending');
                                const res = await fetch(API_URLS.postFormsHubspot, {
                                    body: JSON.stringify(formData),
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                    },
                                });
                                if (!res.ok) {
                                    setStatus('errored');
                                    // @todo: Handle general error state
                                }
                                // Handle post submit actions.
                                if (postSubmitAction.type === 'redirect_url') {
                                    window.location.href = postSubmitAction.value;
                                    return;
                                }
                                setStatus('fulfilled');
                            }, isModal: true }))))))));
};
export default withMotionMax(ModalForm);
